/*
 * TwcCartAttributes — the one place the storefront writes CART ATTRIBUTES.
 *
 * A cart attribute survives into `checkout.attributes`, so the analytics and
 * ad-platform layer reads it on the order. The club writes three:
 *
 *   twc_referred_by  the captured referral code (assets/twc-referral-capture.js)
 *   date_of_birth    the buyer's birth date, always "YYYY-MM-DD"
 *   purchase_type    "gift" on a gifting checkout, absent everywhere else
 *
 * MERGE, NEVER ASSIGN. POST /cart/update.js replaces the whole `attributes`
 * object it receives, so a body that names only one key deletes the others.
 * Every writer therefore calls merge() on ONE shared body. An empty string
 * removes a key: {"attributes":{"purchase_type":""}} returns a cart that
 * keeps date_of_birth and drops purchase_type.
 *
 * A cart permalink (/cart/<variant>:<qty>) carries no body, so it takes its
 * attributes as `?attributes[key]=value` query parameters instead. That path
 * needs no JavaScript and lives in blocks/twc-gift-pack.liquid.
 *
 * CLEAR-FIRST CART WRITES. This file also owns clearThenAdd(), the ONE
 * Ajax Cart API sequence the gift and membership entry points run before
 * they send the buyer to checkout (the sign-up page runs it only as the
 * fallback behind its Storefront API cartCreate — see assets/twc-signup.js):
 *
 *   1. POST /cart/clear.js     empty the cart
 *   2. POST /cart/update.js    write the cart attributes
 *   3. POST /cart/add.js       add the one line the buyer chose
 *
 * The invariant: these flows always start from an empty cart, so a member can
 * never check out a mixed cart from a join or gift entry point. A per-variant
 * zeroing would remove only the lines the flow itself knows about, so
 * anything else the buyer has collected would ride along into a membership
 * or gift checkout.
 *
 * SERIAL, NEVER OVERLAPPING. Each call starts only after the previous
 * response resolves. The Ajax Cart API has no cart version and gives no
 * ordering guarantee between two writes on one cart: a write that starts
 * before the previous response arrives can land first, and its response
 * carries the cart as it was before the other write. A lost clear puts the
 * old membership line back beside the new one, so checkout shows two lines.
 *
 * The update is still a step of its own beside the clear: /cart/clear.js
 * removes the line items and KEEPS the cart attributes and the note, so
 * purchase_type=gift from an earlier gifting checkout survives the clear;
 * only an empty value removes it.
 *
 * Every step is FATAL. The caller's catch shows its own error and does NOT
 * redirect, so a half-written cart never reaches checkout.
 *
 * THE ADD-TO-CART EVENT. Shopify's own pixels see no add_to_cart for these
 * flows: the Ajax add never passes through a cart form, and the sign-up
 * page's Storefront cartCreate fills a cart the Online Store never sees.
 * So after a successful add, and only then, the flow publishes
 * `twc:add_to_cart` through window.twcTrack (snippets/twc-analytics-bridge)
 * and redirects once the bridge has settled (bounded at ~300 ms). The
 * payload is one shape on every path:
 *
 *   { source: 'signup' | 'gift_pack' | 'gift_card',
 *     currency, value,
 *     items: [{ variant_id, product_id, title, variant_title, price,
 *               quantity, selling_plan_id | null }],
 *     page_path }
 *
 * addedItems() builds the { currency, value, items } core from a
 * /cart/add.js body (prices arrive in cents there); the sign-up page builds
 * the same core from its cartCreate lines. trackAdded() adds source and
 * page_path, publishes and returns the settle promise. It NEVER rejects and
 * it never publishes on an error path: a flow calls it from the `then` of
 * its add, after the cart write succeeded, and chains the ONE redirect on
 * the promise it returns. The checkout itself still raises Shopify's own
 * checkout_started; nothing here emits anything of that kind.
 */
(function (global) {
  'use strict';

  if (global.TwcCartAttributes) return;

  function pad(n) {
    return (n < 10 ? '0' : '') + n;
  }

  /*
   * "dd/mm/yyyy" (the club's masked birth-date format) or an already-ISO
   * "YYYY-MM-DD" becomes "YYYY-MM-DD". Anything else becomes "".
   */
  function toIsoDate(value) {
    var text = String(value == null ? '' : value).trim();
    var year;
    var month;
    var day;

    var masked = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text);
    if (masked) {
      day = Number(masked[1]);
      month = Number(masked[2]);
      year = Number(masked[3]);
    } else {
      var iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
      if (!iso) return '';
      year = Number(iso[1]);
      month = Number(iso[2]);
      day = Number(iso[3]);
    }

    // Reject 31/02 and every other overflowed day: JavaScript rolls such a
    // date into the next month, so only a value that survives the round trip
    // unchanged is a real calendar date.
    var probe = new Date(Date.UTC(year, month - 1, day));
    if (
      probe.getUTCFullYear() !== year ||
      probe.getUTCMonth() !== month - 1 ||
      probe.getUTCDate() !== day
    ) {
      return '';
    }

    return year + '-' + pad(month) + '-' + pad(day);
  }

  /*
   * The birth date the join surface knows, or "". Two sources, freshest
   * first:
   *
   *   1. a masked dd/mm/yyyy birth-date input anywhere on the page — the
   *      [data-twc-dob] hook the gift-redeem join panel already carries;
   *   2. data-twc-dob-value on the picker element, which Liquid fills from
   *      the engine-projected customer metafield twc.dob.
   *
   * The join page renders NO birth-date field today. The club captures the
   * birth date inside checkout, in the app's date-of-birth gate, so source 2
   * is the one that fires — a known customer who joins again supplies the
   * date, a brand-new visitor supplies none.
   */
  function dobIso(picker) {
    var typed = global.document.querySelector('[data-twc-dob]');
    var iso = typed ? toIsoDate(typed.value) : '';
    if (iso) return iso;
    return picker ? toIsoDate(picker.getAttribute('data-twc-dob-value')) : '';
  }

  /*
   * Merge attributes into a /cart/update.js body and return that body. This
   * is the only supported way to add a key: an assignment to body.attributes
   * wipes every key the new object does not name.
   */
  function merge(body, attrs) {
    if (!body || !attrs) return body;
    if (!body.attributes) body.attributes = {};
    var into = body.attributes;
    for (var key in attrs) {
      if (Object.prototype.hasOwnProperty.call(attrs, key)) into[key] = attrs[key];
    }
    return body;
  }

  /*
   * One Ajax Cart API POST. The rejected value carries `data` so a caller can
   * show the storefront's own wording (`description` or `message`) before it
   * falls back to local copy.
   */
  function postJson(url, body) {
    // A read-only member preview tab (snippets/twc-preview.liquid) writes no
    // cart: the add-card and gift flows would act for whoever is signed in.
    if (global.TWC_PREVIEW && global.TWC_PREVIEW.active) {
      var api = global.TwcAccountApi;
      var message = api && api.preview ? api.preview.copy('read_only', 'Read-only preview') : 'Read-only preview';
      if (api && api.flash) api.flash(message, 'info');
      var blocked = new Error(message);
      blocked.status = 0;
      blocked.data = { message: message, code: 'PREVIEW_READ_ONLY' };
      return Promise.reject(blocked);
    }
    return global
      .fetch(url, {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify(body),
      })
      .then(function (res) {
        return res
          .json()
          .catch(function () {
            return {};
          })
          .then(function (data) {
            if (!res.ok) {
              var err = new Error(data.description || data.message || 'HTTP ' + res.status);
              err.status = res.status;
              err.data = data;
              throw err;
            }
            return data;
          });
      });
  }

  /*
   * The clear-first cart write (see the header). `root` is Shopify's locale
   * route root, `updateBody` is the /cart/update.js body built with merge()
   * above, and `items` is the /cart/add.js items array.
   *
   * Returns the promise chain. Every rejection reaches the caller, which
   * reports it and stays on the page. A rejected step also stops the steps
   * after it, so no later write reaches the cart.
   *
   * The three calls run one after the other, each one started from the
   * previous response's `then`. The Ajax Cart API gives no ordering guarantee
   * between two writes on one cart, so an overlapping update can land before
   * the clear and put the cleared line back; that lost update makes a second
   * line at checkout.
   *
   * The update is skipped when the caller names no attribute: an empty
   * /cart/update.js body writes nothing, so it is a wasted round trip in
   * front of the buyer.
   */
  function clearThenAdd(root, updateBody, items) {
    var base = root || '/';
    var body = updateBody || {};

    return postJson(base + 'cart/clear.js', {})
      .then(function () {
        return body.attributes ? postJson(base + 'cart/update.js', body) : null;
      })
      .then(function () {
        return postJson(base + 'cart/add.js', { items: items || [] });
      });
  }

  /*
   * An id for the Ajax Cart API. The engine sends the add-card ids as
   * strings; a numeric string goes to the cart as it is (a string keeps an id
   * above 2^53 exact) and a gid keeps its trailing number. Anything else is
   * no id.
   */
  function cartId(value) {
    var m = /(\d+)$/.exec(String(value == null ? '' : value));
    return m ? m[1] : null;
  }

  /*
   * The /cart/add.js line for the engine's me.payment.addCard, or null when
   * the engine sent no usable add-card checkout (an older payload, or a
   * member who already has a subscription contract). The shape is the
   * sign-up page's: one membership variant on its selling plan.
   */
  function addCardItem(addCard) {
    if (!addCard) return null;
    var id = cartId(addCard.variantId);
    var plan = cartId(addCard.sellingPlanId);
    if (!id || !plan) return null;
    return { id: id, quantity: 1, selling_plan: plan };
  }

  /*
   * The add-card checkout: the $0 membership line from me.payment.addCard,
   * written with clearThenAdd(), then /checkout. Checkout vaults the card and
   * creates the subscription contract. A returning member sends no referral
   * code (a referral rewards a NEW member) and no date_of_birth (the club
   * already holds it); purchase_type goes out empty so a gifting checkout
   * left in the cart session cannot mark this one as a gift.
   *
   * Returns the promise chain. It rejects, without writing to the cart, when
   * addCard is unusable, and rejects on any failed cart step; the caller
   * reports the error and stays on the page. On success it navigates and the
   * caller keeps its busy state through the navigation.
   */
  function startAddCard(root, addCard) {
    var item = addCardItem(addCard);
    if (!item) return Promise.reject(new Error('No add-card checkout'));
    return clearThenAdd(root, merge({}, { purchase_type: '' }), [item]).then(function () {
      global.location.href = '/checkout';
    });
  }

  function money(cents) {
    var n = Number(cents);
    return isFinite(n) ? Math.round(n) / 100 : 0;
  }

  function activeCurrency() {
    var shopify = global.Shopify;
    return (shopify && shopify.currency && shopify.currency.active) || null;
  }

  /*
   * The { currency, value, items } core of a twc:add_to_cart payload, from a
   * /cart/add.js response. A multi-item add answers { items: [...] }, a
   * single-item add answers the line itself; both are read. Prices arrive in
   * cents and leave in currency units. selling_plan_id is the plan on the
   * line's selling_plan_allocation, or null for a one-off purchase.
   */
  function addedItems(body) {
    var lines = body && Array.isArray(body.items) ? body.items : body && body.id ? [body] : [];
    var items = [];
    var value = 0;
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i] || {};
      var alloc = line.selling_plan_allocation;
      var plan = alloc && alloc.selling_plan;
      var price = money(line.price);
      var quantity = Number(line.quantity) || 0;
      items.push({
        variant_id: line.id != null ? line.id : null,
        product_id: line.product_id != null ? line.product_id : null,
        title: line.product_title || line.title || null,
        variant_title: line.variant_title || null,
        price: price,
        quantity: quantity,
        selling_plan_id: plan && plan.id != null ? plan.id : null,
      });
      value += price * quantity;
    }
    return { currency: activeCurrency(), value: Math.round(value * 100) / 100, items: items };
  }

  /*
   * Publishes twc:add_to_cart for a finished add and returns the bridge's
   * settle promise, which the caller chains its redirect on. `source` names
   * the flow, `core` is addedItems() or the sign-up page's equivalent.
   * Never rejects: a flow whose cart write succeeded must reach checkout
   * whatever the analytics layer does.
   */
  function trackAdded(source, core) {
    var track = global.twcTrack;
    if (typeof track !== 'function') return Promise.resolve();
    var payload = {
      source: source,
      currency: core && core.currency !== undefined ? core.currency : activeCurrency(),
      value: (core && core.value) || 0,
      items: (core && core.items) || [],
      page_path: global.location && global.location.pathname,
    };
    try {
      return Promise.resolve(track('add_to_cart', payload)).catch(function () {});
    } catch (e) {
      return Promise.resolve();
    }
  }

  global.TwcCartAttributes = {
    toIsoDate: toIsoDate,
    dobIso: dobIso,
    merge: merge,
    clearThenAdd: clearThenAdd,
    addCardItem: addCardItem,
    startAddCard: startAddCard,
    addedItems: addedItems,
    trackAdded: trackAdded,
    // A gifting checkout. Only a gifting entry writes this.
    GIFT: { purchase_type: 'gift' },
  };
})(window);
